import { HZ_RASTER_MVT_CROSSOVER_ZOOM } from "constants/mapLayers.constants";

import type { MapLegendItem } from "api/map";

import type { DashboardCapabilities } from "models/dashboard";

export const MAX_WELLS_FOR_ENTITIES_ON_MAP_FETCH = 100000;
export const MAP_ZOOM_LEVEL_FOR_ENTITIES_ON_MAP_FETCH = 6;
export const MAP_ZOOM_LEVEL_FOR_TYPELOG_ENTITIES_ON_MAP_FETCH = 4;

export function getTotalLegendCount(
  legendItems?: readonly MapLegendItem[] | null
): number {
  if (!legendItems?.length) {
    return 0;
  }

  return legendItems.reduce((total, legendItem) => total + (legendItem?.count ?? 0), 0);
}

export function canFetchEntitiesOnMapAtZoom(params: {
  zoom: number;
  legendItems?: readonly MapLegendItem[] | null;
  capabilities?: Pick<DashboardCapabilities, "hasTypeLogWidget"> | null;
}): boolean {
  const { zoom, legendItems, capabilities } = params;
  const wellsOnMap = getTotalLegendCount(legendItems);
  const minZoom = capabilities?.hasTypeLogWidget
    ? MAP_ZOOM_LEVEL_FOR_TYPELOG_ENTITIES_ON_MAP_FETCH
    : MAP_ZOOM_LEVEL_FOR_ENTITIES_ON_MAP_FETCH;

  return wellsOnMap < MAX_WELLS_FOR_ENTITIES_ON_MAP_FETCH && zoom >= minZoom;
}

// Mapbox's default map maxZoom, and the ZoomSlider's max (react-range throws above it).
const TYPE_LOG_MAX_ZOOM = 22;

// Type log icons draw from the well vector tiles, which carry every well in the txn, so
// below the crossover they are only affordable under the entity-fetch cap. An unknown
// (empty) legend waits for the crossover too.
function getTypeLogMinZoom(legendItems: Maybe<readonly MapLegendItem[]>): number {
  const wellsOnMap = getTotalLegendCount(legendItems);
  return wellsOnMap > 0 && wellsOnMap < MAX_WELLS_FOR_ENTITIES_ON_MAP_FETCH
    ? MAP_ZOOM_LEVEL_FOR_TYPELOG_ENTITIES_ON_MAP_FETCH
    : HZ_RASTER_MVT_CROSSOVER_ZOOM;
}

// The Type Log layers' effective zoom range: a saved range can narrow it but never drop
// below the well-count floor. Shared by useTypeLogWellsLayer and the ZoomSlider.
export function getTypeLogZoomRange(
  legendItems: Maybe<readonly MapLegendItem[]>,
  saved: Maybe<{ minzoom?: number; maxzoom?: number }>
): [number, number] {
  const floor = getTypeLogMinZoom(legendItems);
  const maxzoom = saved?.maxzoom ?? TYPE_LOG_MAX_ZOOM;
  return [Math.min(Math.max(saved?.minzoom ?? floor, floor), maxzoom), maxzoom];
}
